<script setup lang="ts">
import Modal from '~/components/common/modal.vue'
import FilterDropdown from '~/components/mobile/common/filter-dropdown-v2.vue'
import { useModal } from '~/composables/common/use-modal'
import { useCommon } from '~/composables/common/use-common'
import { ModalCollection } from '~/constants'
import { ModalAccountCollection } from '~/constants/account'

const props = withDefaults(
  defineProps<{
    agencyName: string
    accounts?: string[]
    creditRate: number
    iconClass?: string
    variant?: 'green' | 'yellow' | 'light'
    hasNote?: boolean
  }>(),
  { accounts: undefined, iconClass: undefined, variant: 'green', hasNote: false }
)

const { openModal } = useModal()
const { openLiveChat } = useCommon()

const selectModalId = computed(() => `${ModalCollection.FILTER_DROPDOWN_MODAL}_${props.agencyName}_${props.variant}`)
const instructionsModalId = computed(
  () => `${ModalAccountCollection.NEW_ACCOUNT_NOTE}_${props.agencyName}_${props.variant}`
)
</script>

<template>
  <div :class="['agency-wallet', `agency-wallet--${variant}`]">
    <div class="agency-wallet__title">
      <span v-if="iconClass" :class="[iconClass, 'agency-wallet__icon']"></span>
      <span>Ví {{ agencyName }}</span>

      <template v-if="hasNote">
        <img
          src="/assets/images/icons/info.svg"
          alt="help"
          role="button"
          @click="openModal(ModalAccountCollection.AGENCY_WALLET_NOTE)"
        />
  
        <Modal :modalId="ModalAccountCollection.AGENCY_WALLET_NOTE" modalDialogClasses="modal-select-dialog">
          <div class="agency-wallet__modal">
            <h3 class="agency-wallet__modal__title">Ghi chú</h3>
            <p>Cược trung gian <strong>{{ creditRate }}/1 Điểm</strong>.</p>
            <p>
              Để thay đổi tỉ lệ xin vui lòng
              <a class="text-decoration-underline color-primary" href="#" @click.prevent="openLiveChat">Liên hệ CSKH</a>
            </p>
          </div>
        </Modal>
      </template>
    </div>

    <FilterDropdown
      v-if="accounts"
      :label="`Ví ${agencyName}`"
      :modalId="selectModalId"
      :optionItems="accounts.map(value => ({ value, label: value }))"
      :valueSelected="accounts[0]"
    />

    <template v-else>
      <a class="agency-wallet__instructions" @click.prevent="openModal(instructionsModalId)">Hướng dẫn tạo ví</a>
  
      <Modal :modalId="instructionsModalId" modalDialogClasses="modal-select-dialog">
        <div class="agency-wallet__modal">
          <h3 class="agency-wallet__modal__title">Tạo tài khoản ví {{ agencyName }}</h3>
          <p><strong class="color-primary">Bước 1:</strong> Nạp tiền vào ví chính</p>
          <p><strong class="color-primary">Bước 2:</strong> Chuyển tiền vào ví đại lý {{ agencyName }} mới</p>
        </div>
      </Modal>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.agency-wallet {
  width: 100%;

  &__title {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 8px;
    font-weight: 600;
    line-height: 24px;
  }

  &__icon {
    font-size: 20px;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &__modal {
    line-height: 24px;

    &__title {
      margin-bottom: 16px;
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;
    }
  }

  &:deep(.filter-wrapper-dropdown) {
    .label {
      display: none;
    }

    .filter-dropdown__header {
      padding: 0 8px;
      border: none;
    }

    .filter-dropdown__name {
      font-size: 14px;
      font-weight: 700;
      color: $neutral-900;
    }
  }

  &__instructions {
    display: inline-block;
    width: 100%;
    border-radius: 4px;
    text-decoration: underline;
    line-height: 24px;
    background-color: $white;
  }

  &--green {
    .agency-wallet__icon {
      background-image: -webkit-linear-gradient(#a6dc29, #46ee1c);
    }

    &:deep(.filter-dropdown__header) {
      background-color: $green-100;
    }
  }

  &--yellow {
    .agency-wallet__icon {
      background-image: -webkit-linear-gradient(#e3cc00, #d86800);
    }

    &:deep(.filter-dropdown__header) {
      background-color: #fff1c6;
    }
  }

  &--light {
    .agency-wallet {
      &__title {
        font-weight: 400;
        margin-bottom: 4px;
        color: $primary-75;
      }

      &__instructions {
        padding-left: 4px;
      }
    }
  }
}
</style>
